import React, { memo, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { isNil } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  PermissionResource,
  PermissionAction,
  VersionStatusEnum,
} from '@marketmuse/config/types/papi';

import { PAPI } from '@marketmuse/config/types';
import { Icon, SUPPORTED_ICON, Tooltip } from '@marketmuse/components';
import { checkIsOrgAdmin } from '@marketmuse/utilities/access';

import withTailwindCompatibility from '../../../hocs/withTailwindCompatibility';
import IconSvg from '../../../components/IconSvg';
import Popper from '../../../components/Tooltip/Popper';

import { toggleDynamic } from '../../../actions/miscActions';

import { useSuiteNavigate } from '../../../hooks/useSuiteNavigate';
import useHasAccess from '../../../hooks/useHasAccess';

import sidebarAppsWrapper from '../../../utils/wrappers/sidebarAppsWrapper';
import isPageActive from '../../../utils/isPageActive';

import {
  Separator,
  SidebarButton,
  SidebarPaddingLR,
} from '../components/shared';
import QuickStartLinks from '../components/QuickStartLinks';
import SidebarItem from '../components/SidebarItem';
import { AppResearchStatus } from '../components/AppResearchStatus';
import IconAppStatus from '../components/IconAppStatus';

const IconWrapped = withTailwindCompatibility({
  Component: Icon,
  style: { height: '100%' },
});

const InventoryStatusNotifier = withTailwindCompatibility({
  Component: ({ versions, invId }) => {
    const status = useRef(null);

    const args =
      versions?.[0]?.status === VersionStatusEnum.FINISHED &&
      status.current !== VersionStatusEnum.FINISHED
        ? {
            iconName: SUPPORTED_ICON.check,
            iconClassName: 'text-blue-400',
            content: 'Your inventory has finished populating',
          }
        : {
            content: "We're collecting data on your website",
            iconName: SUPPORTED_ICON.lightbulb,
            iconClassName: 'text-gray-600 mm-pulse',
          };

    status.current = versions?.[0]?.status;

    return (
      <Tooltip content={args.content}>
        <div className={`w-4 h-4 ${args.iconClassName}`}>
          <Icon name={args.iconName} color="white" size={28} box={16} />
        </div>
      </Tooltip>
    );
  },
  style: { height: '100%' },
});

const abbreviateNumber = value => {
  if (isNil(value) || value === 0) {
    return void 0;
  }
  const b = value.toPrecision(2).split('e');
  const k = b.length === 1 ? 0 : Math.floor(Math.min(b[1].slice(1), 9) / 3);
  const c = k < 1 ? value.toFixed() : (value / Math.pow(10, k * 3)).toFixed(1);
  const d = c < 0 ? c : Math.abs(c);
  const e = d + ['', 'K', 'M'][k];
  return e;
};

const MainSidebarScreen = ({
  collapsed,
  isAdmin: isMMAdmin,
  setActiveScreen,
  invAllAccess,
}) => {
  const navigate = useSuiteNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const apps = useSelector(state => state.apps);
  const loadings = useSelector(state => state.loadings);
  const versions = useSelector(state => state.filter?.versions);
  const invId = useSelector(state => state.filter?.site?.invId);
  const userOrgRole = useSelector(state => state.user?.orgRole);

  const optimizeStatus = useSelector(state => state?.appOptimize?.ui?.status);
  const pageTopicsCount = useSelector(
    state => state.misc?.counts?.pageTopicsAll,
  );
  const topicsCount = useSelector(state => state.misc?.counts?.topicsAll);
  const pagesCount = useSelector(state => state.misc?.counts?.pagesAll);

  const isPowerUpsPageActive = useSelector(
    state => state.misc?.POWER_UP_PAGE_ACTIVE,
  );

  const hasDocumentsAccess = useHasAccess({
    resource: PermissionResource.DOCUMENTS,
    action: PermissionAction.ACCESS_DATA,
  });

  const hasFullSubscription = useHasAccess({
    resource: PermissionResource.SUBSCRIPTION,
    action: PermissionAction.ACCESS_FULL_DATA,
  });

  const showUpgrade =
    !isMMAdmin && checkIsOrgAdmin(userOrgRole) && hasFullSubscription;

  const {
    isAdminActive,
    isDocumentsActive,
    isPTInventoryActive,
    isPInventoryActive,
    isTInventoryActive,

    isResearchActive,
    isOptimizeActive,
    isTagsActive,
  } = useMemo(
    () => ({
      isAdminActive: isPageActive('/admin'),
      isDocumentsActive: isPageActive('/documents'),
      isPTInventoryActive: isPageActive('/inventory/all'),
      isPInventoryActive: isPageActive('/inventory/pages'),
      isTInventoryActive: isPageActive('/inventory/topics'),
      isResearchActive: isPageActive('/apps/research'),
      isOptimizeActive: isPageActive('/apps/optimize'),
      isTagsActive: isPageActive('/tags'),
    }),
    [pathname],
  );

  return (
    <>
      <SidebarPaddingLR>
        {isMMAdmin && (
          <SidebarItem
            header
            title="Admin"
            active={isAdminActive}
            collapsed={collapsed}
            onClick={() => {
              setActiveScreen('admin');
            }}
            badge={<IconSvg name="arrow-right" size="16" />}
          />
        )}

        {isMMAdmin && <Separator />}

        <QuickStartLinks collapsed={collapsed} />

        {invAllAccess && (
          <>
            <SidebarItem
              subheader
              title="Inventory"
              collapsed={collapsed}
              icon={
                versions?.length !== 1 || !invId ? null : (
                  <InventoryStatusNotifier versions={versions} />
                )
              }
            />
            <SidebarItem
              title="All"
              color="grey35"
              icon={<IconSvg name="inventory" size="16" />}
              collapsed={collapsed}
              dataTrack="sidebar--inventory--page-topics"
              active={isPTInventoryActive}
              onClick={() => navigate('inventory/all')}
              label={abbreviateNumber(pageTopicsCount)}
              labelOrder={3}
            />
            <SidebarItem
              title="Pages"
              color="grey35"
              icon={<IconSvg name="pages" size="16" />}
              collapsed={collapsed}
              dataTrack="sidebar--inventory--pages"
              active={isPInventoryActive}
              onClick={() => navigate('inventory/pages')}
              label={abbreviateNumber(pagesCount)}
              labelOrder={3}
            />
            <SidebarItem
              title="Topics"
              color="grey35"
              icon={<IconSvg name="topics" size="16" />}
              collapsed={collapsed}
              dataTrack="sidebar--inventory--topics"
              active={isTInventoryActive}
              onClick={() => navigate('inventory/topics')}
              label={abbreviateNumber(topicsCount)}
              labelOrder={3}
            />
            <Separator />
          </>
        )}

        {hasDocumentsAccess && (
          <>
            <Popper
              trigger="mouseenter focus"
              tippyTheme="dark"
              placement={'right-end'}
              html={
                <SidebarItem
                  title="All Documents in Organization"
                  color="grey35"
                  icon={<IconWrapped name={SUPPORTED_ICON.documents} />}
                  collapsed={collapsed}
                  dataTrack="sidebar--documents-org"
                  onClick={() => navigate('documents', { omitSiteId: true })}
                  active={false}
                  labelOrder={3}
                />
              }
            >
              <SidebarItem
                title="Documents"
                color="grey35"
                icon={<IconWrapped name={SUPPORTED_ICON.documents} />}
                collapsed={collapsed}
                dataTrack="sidebar--documents-site"
                active={isDocumentsActive}
                onClick={() => navigate('documents')}
                labelOrder={3}
              />
            </Popper>

            <SidebarItem
              title="Tags"
              color="grey35"
              icon={<IconWrapped name={SUPPORTED_ICON.plans} />}
              collapsed={collapsed}
              dataTrack="sidebar--tags"
              active={isTagsActive}
              onClick={() => navigate('tags')}
              labelOrder={3}
            />
            <Separator />
          </>
        )}

        {/* APPLICATIONS */}
        <SidebarItem subheader title="Applications" collapsed={collapsed} />

        <SidebarItem
          title="Research"
          titleStyle={{ whiteSpace: 'nowrap' }}
          active={isResearchActive}
          icon={<IconSvg name="research" size="28" box="16" />}
          collapsed={collapsed}
          dataTrack={`sidebar--apps--research`}
          onClick={() => navigate('apps/research/topic-navigator/topic-model')}
          badge={<AppResearchStatus />}
        />
        <SidebarItem
          title="Optimize"
          titleStyle={{ whiteSpace: 'nowrap' }}
          active={isOptimizeActive}
          icon={<IconSvg name="optimize" size="16" box="16" />}
          collapsed={collapsed}
          dataTrack={`sidebar--apps--optimize`}
          onClick={() => navigate('apps/optimize')}
          badge={<IconAppStatus status={optimizeStatus} />}
        />

        {sidebarAppsWrapper(SidebarItem, {
          list: [PAPI.PermissionResource.NEWSROOM],
          apps,
          collapsed,
          isAdmin: isMMAdmin,
          loadings,
          collapsedIconOnly: false,
          navigate,
        })}
      </SidebarPaddingLR>
      <SidebarPaddingLR>
        <Separator />
        <SidebarItem
          title="Power-ups"
          color="grey35"
          activeColor="white"
          icon={
            <IconSvg
              name="mm-sparkles"
              size="16"
              color={isPowerUpsPageActive ? 'yellowBase' : 'yellowDark1'}
            />
          }
          collapsed={collapsed}
          dataTrack="sidebar--power-ups-page"
          active={isPowerUpsPageActive}
          onClick={() => navigate('power-ups')}
        />
        {showUpgrade && !collapsed && (
          <SidebarButton
            admin={false}
            style={{
              marginTop: 10,
              backgroundColor: '#359ff4',
              marginBottom: '12px',
            }}
            onClick={() =>
              dispatch(
                toggleDynamic({
                  id: 'subscriptionManagement',
                  status: true,
                }),
              )
            }
            data-mms--plan-management="upgradeNow"
          >
            Upgrade Now!
          </SidebarButton>
        )}
      </SidebarPaddingLR>
    </>
  );
};

MainSidebarScreen.propTypes = {
  isAdmin: PropTypes.bool,
  collapsed: PropTypes.bool,
  labelLoading: PropTypes.bool,
  inTransition: PropTypes.bool,
};

export default memo(MainSidebarScreen);
